/* ============================================================
   Bubbas Precision Trimmer — PDP JavaScript
   No external dependencies. Vanilla JS only.
   ============================================================ */

'use strict';

/* ----------------------------------------------------------
   1. COUNTDOWN
   ---------------------------------------------------------- */
function initCountdown() {
  document.querySelectorAll('[data-mm-countdown]').forEach(function(el) {
    var total = parseInt(el.dataset.minutes, 10) * 60;
    var remaining = total;

    function pad(n) { return String(n).padStart(2, '0'); }

    function tick() {
      var h = Math.floor(remaining / 3600);
      var m = Math.floor((remaining % 3600) / 60);
      var s = remaining % 60;
      el.textContent = '00 : ' + pad(h) + ' : ' + pad(m) + ' : ' + pad(s);
      remaining = remaining <= 0 ? total : remaining - 1;
    }

    tick();
    setInterval(tick, 1000);
  });
}

/* ----------------------------------------------------------
   3. MINI REVIEWS CAROUSEL
   ---------------------------------------------------------- */
function initMiniReviews() {
  document.querySelectorAll('[data-mm-mini-reviews]').forEach(function(container) {
    var reviews = Array.from(container.querySelectorAll('[data-review]'));
    if (reviews.length < 2) return;

    var idx = 0;

    function show(next) {
      reviews[idx].hidden = true;
      idx = (next + reviews.length) % reviews.length;
      reviews[idx].hidden = false;
    }

    var prevBtn = container.querySelector('[data-mm-mini-review-prev]');
    var nextBtn = container.querySelector('[data-mm-mini-review-next]');
    if (prevBtn) prevBtn.addEventListener('click', function() { show(idx - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function() { show(idx + 1); });
  });
}

/* ----------------------------------------------------------
   4. FAQ — exclusive one-open-at-a-time
   ---------------------------------------------------------- */
function initFaq() {
  document.querySelectorAll('[data-mm-faq-exclusive]').forEach(function(list) {
    var allDetails = Array.from(list.querySelectorAll('details'));
    allDetails.forEach(function(detail) {
      detail.addEventListener('toggle', function() {
        if (detail.open) {
          allDetails.forEach(function(other) {
            if (other !== detail && other.open) other.removeAttribute('open');
          });
        }
      });
    });
  });
}

/* ----------------------------------------------------------
   5. STICKY ATC
   ---------------------------------------------------------- */
function initStickyAtc() {
  var stickyBar = document.getElementById('mm-sticky-atc');
  var hero = document.getElementById('mm-hero');
  if (!stickyBar || !hero) return;

  var observer = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      if (!entry.isIntersecting) {
        stickyBar.setAttribute('data-visible', '');
        stickyBar.setAttribute('aria-hidden', 'false');
      } else {
        stickyBar.removeAttribute('data-visible');
        stickyBar.setAttribute('aria-hidden', 'true');
      }
    });
  }, { threshold: 0.05 });

  observer.observe(hero);
}

/* ----------------------------------------------------------
   6. CART STATE
   ---------------------------------------------------------- */
// /cart.js does not reliably populate `compare_at_price` on line items
// (Markets currency, B2B, and bundle apps like Kaching often strip it).
// We hydrate it ourselves from /products/{handle}.js, keyed by variant id.
var variantComparePriceCache = {};
var productFetchInFlight = {};

function hydrateVariantComparePrices(items) {
  var handles = {};
  items.forEach(function(it) {
    if (!it.handle) return;
    if (variantComparePriceCache[it.variant_id] !== undefined) return;
    handles[it.handle] = true;
  });
  var pending = Object.keys(handles).map(function(handle) {
    if (productFetchInFlight[handle]) return productFetchInFlight[handle];
    productFetchInFlight[handle] = fetch('/products/' + handle + '.js')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(p) {
        if (!p || !Array.isArray(p.variants)) return;
        p.variants.forEach(function(v) {
          var cap = (typeof v.compare_at_price === 'number') ? v.compare_at_price : null;
          variantComparePriceCache[v.id] = (cap && cap > v.price) ? cap : null;
        });
      })
      .catch(function() {});
    return productFetchInFlight[handle];
  });
  return Promise.all(pending);
}

function getItemComparePrice(item) {
  if (typeof item.compare_at_price === 'number' && item.compare_at_price > item.price) {
    return item.compare_at_price;
  }
  var cached = variantComparePriceCache[item.variant_id];
  return (typeof cached === 'number' && cached > item.price) ? cached : null;
}

var CartState = {
  items: [],
  subtotal: 0,
  discountActive: false,

  async fetch() {
    try {
      var res = await fetch('/cart.js');
      if (!res.ok) return;
      var data = await res.json();
      this.items = data.items || [];
      this.subtotal = data.total_price || 0;
      // Does the cart carry a real discount (cart- or line-level)? When it
      // does, a naive qty × unit projection can't anticipate "buy N for $X"
      // style breaks, so the optimistic subtotal defers to the server instead
      // of flashing a wrong number. NOTE: a plain compare-at "sale" is not a
      // discount — original_line_price still equals final_line_price — so it
      // won't trip this flag.
      this.discountActive =
        (typeof data.total_discount === 'number' && data.total_discount > 0) ||
        this.items.some(function (it) {
          if (it.original_line_price && it.final_line_price && it.original_line_price !== it.final_line_price) return true;
          if (typeof it.total_discount === 'number' && it.total_discount > 0) return true;
          if (it.line_level_discount_allocations && it.line_level_discount_allocations.length > 0) return true;
          return false;
        });
      this.updateBadge();
      this.renderItems();
      this.updateShippingBar();
      this.updateGiftBar();
      // Notify any listeners (sticky ATC, external apps, analytics).
      document.dispatchEvent(new CustomEvent('mm:cart:updated', { detail: data }));
      // Hydrate compare-at prices in the background, then re-render so
      // the savings strikethrough / "Save $X" badge appears once the
      // variant data arrives. First render is unblocked.
      var self = this;
      hydrateVariantComparePrices(this.items).then(function() {
        self.renderItems();
      });
    } catch (e) {
      /* silent */
    }
  },

  updateShippingBar() {
    this.updateShippingRow();
    var bar = document.getElementById('mm-cart-ship');
    if (!bar) return;
    var threshold = parseInt(bar.dataset.threshold, 10) || 0;
    if (!threshold) { bar.hidden = true; return; }
    var remaining = Math.max(0, threshold - this.subtotal);
    var pct = Math.min(100, (this.subtotal / threshold) * 100);
    var fill = bar.querySelector('[data-mm-ship-fill]');
    var msg  = bar.querySelector('[data-mm-ship-msg]');
    if (fill) fill.style.width = pct + '%';
    if (msg) {
      // Templates are merchant-defined in PDP Header settings.
      // {amount} is replaced with the formatted remaining $.
      var tplProgress = bar.dataset.textProgress || 'You’re {amount} away from free shipping';
      var tplUnlocked = bar.dataset.textUnlocked || '🎉 You unlocked <strong>free shipping</strong>!';
      var amount = '<strong>' + formatMoney(remaining) + '</strong>';
      msg.innerHTML = remaining === 0
        ? tplUnlocked
        : tplProgress.replace(/\{amount\}/g, amount);
    }
    if (remaining === 0) bar.setAttribute('data-unlocked', '');
    else bar.removeAttribute('data-unlocked');
    bar.hidden = false;
  },

  updateShippingRow() {
    var row = document.getElementById('mm-cart-shipping-row');
    if (!row) return;
    var threshold = parseInt(row.dataset.threshold, 10) || 0;
    // No threshold configured → always show "Free"
    // Threshold configured → only show once subtotal meets it
    var unlocked = !threshold || this.subtotal >= threshold;
    row.hidden = !unlocked;
  },

  updateGiftBar() {
    var bar = document.getElementById('mm-cart-gift');
    if (!bar) return;
    var threshold = parseInt(bar.dataset.threshold, 10) || 0;
    var label = bar.dataset.label || 'free gift';
    if (!threshold) { bar.hidden = true; return; }
    var remaining = Math.max(0, threshold - this.subtotal);
    var pct = Math.min(100, (this.subtotal / threshold) * 100);
    var fill = bar.querySelector('[data-mm-gift-fill]');
    var msg  = bar.querySelector('[data-mm-gift-msg]');
    if (fill) fill.style.width = pct + '%';
    if (msg) {
      msg.innerHTML = remaining === 0
        ? '🎁 You unlocked your <strong>' + label + '</strong>!'
        : 'Add <strong>' + formatMoney(remaining) + '</strong> for a <strong>' + label + '</strong>';
    }
    bar.hidden = false;
  },

  updateBadge() {
    var count = this.items.reduce(function(sum, item) { return sum + item.quantity; }, 0);
    document.querySelectorAll('[data-mm-cart-count]').forEach(function(el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
    // Keep the cart-page "Your Cart (N items)" heading in sync when the
    // drawer's own qty controls are the source of truth — e.g. user is
    // sitting on /cart and adjusts qty inside the drawer. Subtotal/total
    // on the cart page are intentionally left to its own handler so
    // discount semantics stay correct.
    document.querySelectorAll('[data-mm-cart-item-count]').forEach(function(el) { el.textContent = count; });
    document.querySelectorAll('[data-mm-cart-item-word]').forEach(function(el) {
      el.textContent = count === 1 ? 'item' : 'items';
    });
    // Toggle the cart-icon animation when items are present.
    document.querySelectorAll('.mm-nav__icon--cart[data-mm-cart-anim]').forEach(function(btn) {
      if (count > 0) btn.classList.add('mm-cart-anim--active');
      else btn.classList.remove('mm-cart-anim--active');
    });
  },

  renderItems() {
    var container = document.getElementById('mm-cart-items');
    var footer = document.getElementById('mm-cart-footer');
    var empty = document.getElementById('mm-cart-empty');
    var reserve = document.querySelector('[data-mm-reserve]');
    var subtotalEl = document.getElementById('mm-cart-subtotal');
    if (!container) return;

    if (this.items.length === 0) {
      container.querySelectorAll('.mm-cart__item').forEach(function(el) { el.remove(); });
      if (empty) empty.hidden = false;
      if (footer) footer.hidden = true;
      if (reserve) reserve.hidden = true;
      CartReserve.stop();
      return;
    }

    if (empty) empty.hidden = true;
    if (footer) footer.hidden = false;
    if (reserve) reserve.hidden = false;

    var drawerEl = document.getElementById('mm-cart');
    var savingsMode = (drawerEl && drawerEl.dataset.savingsMode) || 'strikethrough';

    var html = this.items.map(function(item) {
      var isProtection = (item.properties && item.properties._protection) ? true : false;
      var imgSrc = item.featured_image && item.featured_image.url
        ? item.featured_image.url.replace(/\.([^.]+)$/, '_120x120.$1')
        : '';
      var media = isProtection
        ? '<div class="mm-cart__item-img mm-cart__item-shield" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
              '<path d="M12 2.5l8 3.2v6.1c0 4.4-3.4 8.4-8 9.7-4.6-1.3-8-5.3-8-9.7V5.7l8-3.2z"/>' +
              '<path d="M9 12l2 2 4-4"/>' +
            '</svg>' +
          '</div>'
        : (imgSrc
            ? '<img class="mm-cart__item-img" src="' + imgSrc + '" alt="' + escapeHtml(item.title) + '" width="72" height="72" loading="lazy">'
            : '<div class="mm-cart__item-img mm-ph" data-label="ITEM"></div>');
      var subline;
      if (isProtection) {
        subline = '<div class="mm-cart__item-pill">Add-on · Shipping Protection</div>';
      } else {
        var sublineParts = '';
        if (item.variant_title && item.variant_title !== 'Default Title') {
          sublineParts += '<div class="mm-cart__item-variant">' + escapeHtml(item.variant_title) + '</div>';
        }
        if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.name) {
          sublineParts += '<div class="mm-cart__item-plan">' + escapeHtml(item.selling_plan_allocation.selling_plan.name) + '</div>';
        }
        if (item.properties) {
          var propsHtml = '';
          for (var pk in item.properties) {
            if (!Object.prototype.hasOwnProperty.call(item.properties, pk)) continue;
            if (pk.charAt(0) === '_') continue;
            var pv = item.properties[pk];
            if (pv === null || pv === undefined || pv === '') continue;
            propsHtml += '<li><strong>' + escapeHtml(pk) + ':</strong> ' + escapeHtml(String(pv)) + '</li>';
          }
          if (propsHtml) {
            sublineParts += '<ul class="mm-cart__item-props" role="list">' + propsHtml + '</ul>';
          }
        }
        subline = sublineParts;
      }
      var qty = isProtection
        ? '<button class="mm-cart__item-remove" data-mm-action="cart-qty" data-key="' + item.key + '" data-delta="-' + item.quantity + '" aria-label="Remove shipping protection">Remove</button>'
        : '<div class="mm-cart__item-qty" role="group" aria-label="Quantity">' +
            '<button class="mm-cart__qty-btn mm-cart__qty-btn--dec" data-mm-action="cart-qty" data-key="' + item.key + '" data-delta="-1" aria-label="Remove one">−</button>' +
            '<span class="mm-cart__qty-val">' + item.quantity + '</span>' +
            '<button class="mm-cart__qty-btn mm-cart__qty-btn--inc" data-mm-action="cart-qty" data-key="' + item.key + '" data-delta="1" aria-label="Add one">+</button>' +
          '</div>';
      return '<div class="mm-cart__item' + (isProtection ? ' mm-cart__item--protection' : '') + '" data-key="' + item.key + '">' +
        media +
        '<div class="mm-cart__item-meta">' +
          renderItemSaveBadge(item) +
          '<div class="mm-cart__item-name">' + escapeHtml(item.title) + '</div>' +
          subline +
          qty +
        '</div>' +
        renderItemPrice(item, savingsMode) +
      '</div>';
    }).join('');

    var existingItems = container.querySelectorAll('.mm-cart__item');
    existingItems.forEach(function(el) { el.remove(); });
    if (empty) {
      container.insertAdjacentHTML('afterbegin', html);
    } else {
      container.innerHTML = html;
    }

    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(this.subtotal);
      subtotalEl.classList.remove('is-updating');
    }
  }
};

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

// Line-level prices. Each line shows its *total* (qty × unit, after any
// discount) so the lines add up to the subtotal, plus a "$X each" hint when
// the quantity is more than one. `now`/`was` are line totals; `unit` is the
// per-pair price for the hint.
function computeItemSavings(item) {
  var qty = item.quantity && item.quantity > 0 ? item.quantity : 1;
  // Line total after discounts — congruent with the cart subtotal.
  var now = (typeof item.final_line_price === 'number')
    ? item.final_line_price
    : item.price * qty;
  var was = null;
  if (item.original_line_price && item.original_line_price > item.final_line_price) {
    was = item.original_line_price;
  }
  if (!was) {
    var cap = getItemComparePrice(item); // per-unit compare-at
    if (cap && cap * qty > now) was = cap * qty;
  }
  var unit = Math.round(now / qty);
  return { now: now, was: was, unit: unit, qty: qty };
}

function renderItemSaveBadge(item) {
  var s = computeItemSavings(item);
  if (!s.was) return '';
  var saved = s.was - s.now;
  if (saved <= 0) return '';
  return '<span class="mm-cart__item-save">Save ' + formatMoney(saved) + '</span>';
}

function renderItemPrice(item, mode) {
  var s = computeItemSavings(item);
  var showWas = s.was && mode !== 'off';
  var eachHtml = s.qty > 1
    ? '<span class="mm-cart__item-each">' + formatMoney(s.unit) + ' each</span>'
    : '';
  var wasHtml = showWas
    ? '<span class="mm-cart__item-was">' + formatMoney(s.was) + '</span>'
    : '';
  // Stack vertically whenever there's a secondary line (a "was" or "$X each").
  var stacked = (wasHtml || eachHtml) ? ' mm-cart__item-price--stacked' : '';
  return '<div class="mm-cart__item-price' + stacked + '">' +
    wasHtml +
    '<span class="mm-cart__item-now">' + formatMoney(s.now) + '</span>' +
    eachHtml +
  '</div>';
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* ----------------------------------------------------------
   7. CART DRAWER
   ---------------------------------------------------------- */
var cartDrawerOpen = false;
var lastFocusedCart = null;
var scrollLockY = 0;
var scrollLockCount = 0;

function lockBodyScroll() {
  if (scrollLockCount++ > 0) return;
  scrollLockY = window.scrollY || window.pageYOffset || 0;
  var body = document.body;
  body.style.position = 'fixed';
  body.style.top = '-' + scrollLockY + 'px';
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';
}

function unlockBodyScroll() {
  if (scrollLockCount > 0) scrollLockCount--;
  if (scrollLockCount > 0) return;
  var body = document.body;
  body.style.position = '';
  body.style.top = '';
  body.style.left = '';
  body.style.right = '';
  body.style.width = '';
  body.style.overflow = '';
  window.scrollTo(0, scrollLockY);
}

/* Reservation countdown — sessionStorage-backed cart-hold timer.
   The timer starts the first time the drawer opens this session, persists
   across page navigations, and shows an expired state at 00:00. */
var CartReserve = (function () {
  var STORAGE_KEY = 'mm-cart-reserve-expiry-v1';
  var el = null, labelEl = null, timeEl = null;
  var activeLabel = '', expiredLabel = '', minutes = 10;
  var expiry = 0;
  var iv = null;

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function read() {
    try {
      var v = parseInt(sessionStorage.getItem(STORAGE_KEY), 10);
      return isNaN(v) ? 0 : v;
    } catch (e) { return 0; }
  }
  function write(v) {
    try { sessionStorage.setItem(STORAGE_KEY, String(v)); } catch (e) {}
  }

  function render() {
    if (!el || !timeEl) return;
    var rem = expiry - Date.now();
    if (rem <= 0) {
      timeEl.textContent = '00:00';
      el.setAttribute('data-expired', '');
      if (labelEl && expiredLabel) labelEl.textContent = expiredLabel;
      stop();
      return;
    }
    el.removeAttribute('data-expired');
    if (labelEl && activeLabel) labelEl.textContent = activeLabel;
    var mins = Math.floor(rem / 60000);
    var secs = Math.floor((rem % 60000) / 1000);
    timeEl.textContent = pad(mins) + ':' + pad(secs);
  }

  function start() {
    if (!el) init();
    if (!el) return;
    if (!expiry) {
      expiry = read();
      if (!expiry || expiry < Date.now()) {
        expiry = Date.now() + minutes * 60 * 1000;
        write(expiry);
      }
    }
    render();
    if (iv) return;
    iv = setInterval(render, 1000);
  }
  function stop() {
    if (iv) { clearInterval(iv); iv = null; }
  }

  function init() {
    el = document.querySelector('[data-mm-reserve]');
    if (!el) return;
    labelEl = el.querySelector('[data-mm-reserve-label]');
    timeEl  = el.querySelector('[data-mm-reserve-time]');
    activeLabel  = el.getAttribute('data-active-label')  || '';
    expiredLabel = el.getAttribute('data-expired-label') || '';
    minutes = parseInt(el.getAttribute('data-minutes'), 10) || 10;
  }

  return { start: start, stop: stop, init: init };
})();

function openCart() {
  var drawer = document.getElementById('mm-cart');
  var scrim = document.querySelector('[data-mm-cart-scrim]');
  if (!drawer) return;

  lastFocusedCart = document.activeElement;
  drawer.setAttribute('data-open', '');
  drawer.setAttribute('aria-hidden', 'false');
  if (scrim) { scrim.setAttribute('data-open', ''); scrim.setAttribute('aria-hidden', 'false'); }
  lockBodyScroll();
  cartDrawerOpen = true;

  CartReserve.start();

  requestAnimationFrame(function() {
    var closeBtn = drawer.querySelector('[data-mm-action="close-cart"]');
    if (closeBtn) closeBtn.focus();
  });

  trapFocus(drawer);
}

function closeCart() {
  var drawer = document.getElementById('mm-cart');
  var scrim = document.querySelector('[data-mm-cart-scrim]');
  if (!drawer) return;

  drawer.removeAttribute('data-open');
  drawer.setAttribute('aria-hidden', 'true');
  if (scrim) { scrim.removeAttribute('data-open'); scrim.setAttribute('aria-hidden', 'true'); }
  unlockBodyScroll();
  cartDrawerOpen = false;

  CartReserve.stop();

  removeTrapFocus(drawer);
  if (lastFocusedCart) { lastFocusedCart.focus(); lastFocusedCart = null; }
}

/* ----------------------------------------------------------
   8. MOBILE DRAWER
   ---------------------------------------------------------- */
var navDrawerOpen = false;
var lastFocusedDrawer = null;

function openDrawer() {
  var drawer = document.getElementById('mm-drawer');
  var scrim = document.querySelector('[data-mm-drawer-scrim]');
  var btn = document.querySelector('[data-mm-action="open-drawer"]');
  if (!drawer) return;

  lastFocusedDrawer = document.activeElement;
  drawer.setAttribute('data-open', '');
  drawer.setAttribute('aria-hidden', 'false');
  if (scrim) scrim.setAttribute('data-open', '');
  if (btn) btn.setAttribute('aria-expanded', 'true');
  lockBodyScroll();
  navDrawerOpen = true;

  requestAnimationFrame(function() { drawer.focus(); });
  trapFocus(drawer);
}

function closeDrawer() {
  var drawer = document.getElementById('mm-drawer');
  var scrim = document.querySelector('[data-mm-drawer-scrim]');
  var btn = document.querySelector('[data-mm-action="open-drawer"]');
  if (!drawer) return;

  drawer.removeAttribute('data-open');
  drawer.setAttribute('aria-hidden', 'true');
  if (scrim) scrim.removeAttribute('data-open');
  if (btn) btn.setAttribute('aria-expanded', 'false');
  unlockBodyScroll();
  navDrawerOpen = false;

  removeTrapFocus(drawer);
  if (lastFocusedDrawer) { lastFocusedDrawer.focus(); lastFocusedDrawer = null; }
}

/* ----------------------------------------------------------
   9. FOCUS TRAP
   ---------------------------------------------------------- */
var focusTrapHandler = null;

function trapFocus(el) {
  var focusable = el.querySelectorAll(
    'a[href], button:not([disabled]), input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
  );
  var first = focusable[0];
  var last = focusable[focusable.length - 1];

  removeTrapFocus(el);

  focusTrapHandler = function(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  el.addEventListener('keydown', focusTrapHandler);
}

function removeTrapFocus(el) {
  if (focusTrapHandler) {
    el.removeEventListener('keydown', focusTrapHandler);
    focusTrapHandler = null;
  }
}

/* ----------------------------------------------------------
   10. ADD TO CART
   ---------------------------------------------------------- */
async function addToCart(variantIds) {
  if (!variantIds) {
    showCartError('Please select a variant before adding to cart.');
    return;
  }

  var ids = String(variantIds).split(',').map(function(id) { return id.trim(); }).filter(Boolean);
  if (!ids.length) {
    showCartError('Please select a variant before adding to cart.');
    return;
  }

  var body = JSON.stringify({ items: ids.map(function(id) { return { id: parseInt(id, 10), quantity: 1 }; }) });

  try {
    var res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: body
    });

    if (!res.ok) {
      var err = await res.json();
      showCartError(err.description || 'Could not add item to cart. Please try again.');
      return;
    }

    await CartState.fetch();
    openCart();
    clearCartError();
  } catch (e) {
    showCartError('A network error occurred. Please check your connection and try again.');
  }
}

/* ----------------------------------------------------------
   10a. PRODUCT FORM (real <form action="/cart/add">)
   ---------------------------------------------------------- */
function lockSubmit(form) {
  if (form.__mmLocked) return false;
  form.__mmLocked = true;
  var btn = form.querySelector('button[type="submit"], [data-mm-action="atc"]');
  if (btn) {
    btn.dataset.mmOrigText = btn.dataset.mmOrigText || (btn.querySelector('.mm-btn__label') ? btn.querySelector('.mm-btn__label').textContent : btn.textContent);
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    var labelEl = btn.querySelector('.mm-btn__label') || btn;
    labelEl.textContent = 'Adding…';
  }
  return true;
}
function unlockSubmit(form) {
  form.__mmLocked = false;
  var btn = form.querySelector('button[type="submit"], [data-mm-action="atc"]');
  if (btn) {
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
    if (btn.dataset.mmOrigText) {
      var labelEl = btn.querySelector('.mm-btn__label') || btn;
      labelEl.textContent = btn.dataset.mmOrigText;
    }
  }
}

function initProductForms() {
  document.querySelectorAll('[data-mm-product-form]').forEach(function(form) {
    form.addEventListener('submit', async function(e) {
      e.preventDefault();
      if (!lockSubmit(form)) return; // already in flight — ignore rage-clicks
      var bundleMode = form.dataset.mmBundleMode;
      var kitVariants = form.dataset.mmKitVariants;

      try {
        // Kit mode: multi-add via items[]
        if (bundleMode === 'kit' && kitVariants) {
          await addToCart(kitVariants);
          return;
        }

        // Single mode: post the form's own field set
        var fd = new FormData(form);
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd
        });
        if (!res.ok) {
          var err = await res.json();
          showCartError(err.description || 'Could not add item to cart. Please try again.');
          return;
        }
        await CartState.fetch();
        openCart();
        clearCartError();
      } catch (err) {
        showCartError('A network error occurred. Please check your connection and try again.');
      } finally {
        unlockSubmit(form);
      }
    });
  });
}

/* ----------------------------------------------------------
   10b. VARIANT PICKER — match selected options to a variant
   ---------------------------------------------------------- */
function initVariantPickers() {
  document.querySelectorAll('[data-mm-product-form]').forEach(function(form) {
    var jsonScript = form.querySelector('[data-mm-variants-json]');
    var hiddenId = form.querySelector('[data-mm-variant-id]');
    if (!jsonScript || !hiddenId) return;

    var variants;
    try { variants = JSON.parse(jsonScript.textContent); } catch (e) { return; }

    var optionInputs = Array.from(form.querySelectorAll('[data-mm-option-input]'));
    if (!optionInputs.length) return;

    // Variant-value → image-override map (built from <variant_image> blocks)
    var imageOverrides = [];
    var overridesScript = form.querySelector('[data-mm-variant-image-overrides]');
    if (overridesScript) {
      try { imageOverrides = JSON.parse(overridesScript.textContent) || []; } catch (e) { imageOverrides = []; }
    }
    // Cache original src so we can revert when the merchant doesn't define an override for the selected value
    var imageTargets = Array.from(form.querySelectorAll('[data-mm-variant-image-target]'));
    imageTargets.forEach(function(img) {
      if (!img.dataset.mmVariantImageOriginal) img.dataset.mmVariantImageOriginal = img.getAttribute('src') || '';
    });

    function currentSelection() {
      var groups = {};
      optionInputs.forEach(function(input) {
        // Selects always contribute their value; radios only when checked.
        if (input.tagName !== 'SELECT' && !input.checked) return;
        var i = input.dataset.mmOptionInput;
        groups[i] = input.value;
      });
      // Build ordered array based on option index
      var keys = Object.keys(groups).sort(function(a,b){ return parseInt(a,10) - parseInt(b,10); });
      return keys.map(function(k) { return groups[k]; });
    }

    function variantOpts(v) {
      if (Array.isArray(v.options)) return v.options;
      return [v.option1, v.option2, v.option3].filter(function(x){ return x != null; });
    }

    function update() {
      var sel = currentSelection();
      var match = variants.find(function(v) {
        var vo = variantOpts(v);
        return vo.length === sel.length && vo.every(function(opt, i) { return opt === sel[i]; });
      });
      if (!match) return;

      hiddenId.value = match.id;

      // Update displayed option labels
      form.querySelectorAll('[data-mm-option-display]').forEach(function(el) {
        var idx = parseInt(el.dataset.mmOptionDisplay, 10);
        if (sel[idx] != null) el.textContent = sel[idx];
      });

      // Update price + sticky atc price if the price changed
      var priceEls = document.querySelectorAll('[data-mm-live-price], .mm-offer__price, .mm-sticky-atc__price');
      if (match.price != null) {
        priceEls.forEach(function(el) { el.textContent = formatMoney(match.price); });
      }
      var wasEls = document.querySelectorAll('.mm-offer__was');
      if (match.compare_at_price && match.compare_at_price > match.price) {
        wasEls.forEach(function(el) { el.textContent = formatMoney(match.compare_at_price); el.hidden = false; });
      } else {
        wasEls.forEach(function(el) { el.hidden = true; });
      }

      // Variant-image override — match any selected option value against the merchant's overrides
      if (imageOverrides.length && imageTargets.length) {
        var overrideSrc = null;
        for (var i = 0; i < sel.length && !overrideSrc; i++) {
          var found = imageOverrides.find(function(o) { return o.value === sel[i]; });
          if (found) overrideSrc = found.src;
        }
        imageTargets.forEach(function(img) {
          var newSrc = overrideSrc || img.dataset.mmVariantImageOriginal;
          if (newSrc && img.getAttribute('src') !== newSrc) img.setAttribute('src', newSrc);
        });
      }
    }

    optionInputs.forEach(function(input) { input.addEventListener('change', update); });
    update();
  });
}

/* ----------------------------------------------------------
   10c. UPSELL QUICK-ADD
   ---------------------------------------------------------- */
async function applyPromoCode() {
  var input = document.querySelector('[data-mm-promo-input]');
  var msg   = document.querySelector('[data-mm-promo-msg]');
  if (!input || !input.value.trim()) return;
  var code = input.value.trim();
  if (msg) { msg.hidden = true; msg.removeAttribute('data-state'); }
  try {
    // Shopify natively applies discounts via /discount/<code> redirect.
    // Persist to a cart attribute so it survives drawer navigation.
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: { 'discount_code': code } })
    });
    if (msg) {
      msg.textContent = 'Code saved — applied at checkout.';
      msg.removeAttribute('data-state');
      msg.hidden = false;
    }
  } catch (e) {
    if (msg) {
      msg.textContent = 'Could not apply code. Please try again.';
      msg.setAttribute('data-state', 'error');
      msg.hidden = false;
    }
  }
}

async function quickAddToCart(button) {
  // Hard re-entry guard: if button is mid-flight, ignore subsequent clicks.
  if (button.disabled || button.dataset.mmBusy === '1') return;
  var id = button.dataset.variant;
  if (!id) return;
  var idNum = parseInt(id, 10);

  // Capture original label exactly once and stash on the element so repeated
  // clicks during the "Added" state can't overwrite it with "Adding…".
  if (!button.dataset.mmOrigLabel) {
    button.dataset.mmOrigLabel = button.textContent.trim();
  }
  var orig = button.dataset.mmOrigLabel;

  button.dataset.mmBusy = '1';
  button.disabled = true;
  button.textContent = 'Adding…';

  var reset = function(label, errState) {
    button.textContent = label || orig;
    if (errState) button.setAttribute('data-error', '');
    setTimeout(function() {
      button.textContent = orig;
      button.disabled = false;
      button.dataset.mmBusy = '';
      button.removeAttribute('data-error');
    }, 1500);
  };

  // Belt-and-suspenders timeout — guarantees the button never sticks at "Adding…"
  // even if /cart.js hangs (theme editor previews sometimes do).
  var safetyTimer = setTimeout(function() { reset('Error', true); }, 8000);

  // If this variant is already in the cart, bump its quantity instead of
  // letting Shopify create a second card. We merge even into lines with
  // tracking properties (e.g. __kaching_bundles) — those mark the same
  // product, not an add-on. Add-on lines like shipping protection live on
  // a different variant so the variant_id check alone keeps us out of them.
  // Subscription lines are the one exception: never bump a selling-plan
  // line from a one-time upsell add.
  var existing = null;
  for (var i = 0; i < CartState.items.length; i++) {
    var it = CartState.items[i];
    if (it.variant_id !== idNum) continue;
    if (it.selling_plan_allocation) continue;
    existing = it;
    break;
  }

  try {
    var res;
    if (existing) {
      res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: existing.key, quantity: existing.quantity + 1 })
      });
    } else {
      res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: idNum, quantity: 1 })
      });
    }
    if (!res.ok) {
      clearTimeout(safetyTimer);
      reset('Error', true);
      return;
    }
    // Best-effort cart refresh — never block the success path on it.
    try { await CartState.fetch(); } catch (e) { /* swallow */ }
    clearTimeout(safetyTimer);
    reset('✓ Added');
  } catch (e) {
    clearTimeout(safetyTimer);
    reset('Error', true);
  }
}

function showCartError(msg) {
  var el = document.getElementById('mm-cart-error');
  if (el) { el.textContent = msg; el.hidden = false; }
}

function clearCartError() {
  var el = document.getElementById('mm-cart-error');
  if (el) { el.textContent = ''; el.hidden = true; }
}

var __mmQtyPending = Object.create(null); // key -> { target, sent, item }

function patchLineDom(key, qty, project) {
  var row = document.querySelector('.mm-cart__item[data-key="' + key + '"]');
  if (!row) return;
  var qv = row.querySelector('.mm-cart__qty-val');
  if (qv) qv.textContent = qty;

  var priceEl = row.querySelector('.mm-cart__item-price');
  var item = CartState.items.find(function (i) { return i.key === key; });

  if (item && project) {
    // Safe to project (no active discount, quantity going down): the line
    // total scales linearly with quantity, so qty × unit matches the server.
    var unitNow = (item.quantity > 0 && typeof item.final_line_price === 'number')
      ? Math.round(item.final_line_price / item.quantity)
      : item.price;
    var unitWas = 0;
    if (item.original_line_price && item.quantity && item.original_line_price > item.final_line_price) {
      unitWas = Math.round(item.original_line_price / item.quantity);
    }
    if (!unitWas) {
      var cap = getItemComparePrice(item);
      if (cap && cap > unitNow) unitWas = cap;
    }

    var lineNow = unitNow * qty;
    var lineWas = unitWas * qty;
    var nowEl = row.querySelector('.mm-cart__item-now');
    var wasEl = row.querySelector('.mm-cart__item-was');
    var saveEl = row.querySelector('.mm-cart__item-save');
    var eachEl = row.querySelector('.mm-cart__item-each');

    if (nowEl) nowEl.textContent = formatMoney(lineNow);
    if (wasEl && lineWas) wasEl.textContent = formatMoney(lineWas);
    if (saveEl && lineWas) {
      saveEl.textContent = 'Save ' + formatMoney(lineWas - lineNow);
    }
    if (eachEl) {
      eachEl.textContent = formatMoney(unitNow) + ' each';
      eachEl.hidden = qty <= 1;
    }
    if (priceEl) priceEl.classList.remove('is-updating');
  } else if (priceEl) {
    // Can't safely project (discount in play, or quantity going up where a
    // "buy N for $X" break might kick in) — dim the line total and let the
    // server fetch repaint the exact, discount-aware figures.
    priceEl.classList.add('is-updating');
  }

  if (qty <= 0) {
    row.style.transition = 'opacity .18s ease';
    row.style.opacity = '0.4';
    row.style.pointerEvents = 'none';
  } else {
    row.style.opacity = '';
    row.style.pointerEvents = '';
  }
}

// Hold money values in a dimmed "recalculating" state until the server
// returns the authoritative, discount-aware figures — so we never flash a
// number we'd have to correct a moment later.
function markMoneyPending(key) {
  var subtotalEl = document.getElementById('mm-cart-subtotal');
  if (subtotalEl) subtotalEl.classList.add('is-updating');
  var row = document.querySelector('.mm-cart__item[data-key="' + key + '"]');
  if (row) {
    var priceEl = row.querySelector('.mm-cart__item-price');
    if (priceEl) priceEl.classList.add('is-updating');
  }
}

function patchSubtotalDom() {
  // Discount-aware optimistic subtotal: prefer the per-unit price
  // *after* line-level discounts (final_line_price / quantity) so the
  // displayed total matches what the server will return.
  var total = CartState.items.reduce(function (sum, it) {
    var pendingTarget = __mmQtyPending[it.key] ? __mmQtyPending[it.key].target : it.quantity;
    var unit = (it.quantity > 0 && typeof it.final_line_price === 'number')
      ? (it.final_line_price / it.quantity)
      : it.price;
    return sum + unit * pendingTarget;
  }, 0);
  var subtotalEl = document.getElementById('mm-cart-subtotal');
  if (subtotalEl) {
    subtotalEl.textContent = formatMoney(total);
    subtotalEl.classList.remove('is-updating');
  }
  // Drive shipping + gift bars off the same optimistic subtotal so
  // they animate in lockstep with each click instead of lagging the
  // server round-trip.
  patchShippingBarOptimistic(total);
  patchGiftBarOptimistic(total);
}

function patchShippingBarOptimistic(subtotal) {
  var bar = document.getElementById('mm-cart-ship');
  if (bar && !bar.hidden) {
    var threshold = parseInt(bar.dataset.threshold, 10) || 0;
    if (threshold) {
      var remaining = Math.max(0, threshold - subtotal);
      var pct = Math.min(100, (subtotal / threshold) * 100);
      var fill = bar.querySelector('[data-mm-ship-fill]');
      var msg  = bar.querySelector('[data-mm-ship-msg]');
      if (fill) fill.style.width = pct + '%';
      if (msg) {
        var tplProgress = bar.dataset.textProgress || 'You’re {amount} away from free shipping';
        var tplUnlocked = bar.dataset.textUnlocked || '🎉 You unlocked <strong>free shipping</strong>!';
        var amount = '<strong>' + formatMoney(remaining) + '</strong>';
        msg.innerHTML = remaining === 0 ? tplUnlocked : tplProgress.replace(/\{amount\}/g, amount);
      }
    }
  }
  var row = document.getElementById('mm-cart-shipping-row');
  if (row) {
    var t = parseInt(row.dataset.threshold, 10) || 0;
    row.hidden = !(!t || subtotal >= t);
  }
}

function patchGiftBarOptimistic(subtotal) {
  var bar = document.getElementById('mm-cart-gift');
  if (!bar || bar.hidden) return;
  var threshold = parseInt(bar.dataset.threshold, 10) || 0;
  if (!threshold) return;
  var label = bar.dataset.label || 'free gift';
  var remaining = Math.max(0, threshold - subtotal);
  var pct = Math.min(100, (subtotal / threshold) * 100);
  var fill = bar.querySelector('[data-mm-gift-fill]');
  var msg  = bar.querySelector('[data-mm-gift-msg]');
  if (fill) fill.style.width = pct + '%';
  if (msg) {
    msg.innerHTML = remaining === 0
      ? '🎁 You unlocked your <strong>' + label + '</strong>!'
      : 'Add <strong>' + formatMoney(remaining) + '</strong> for a <strong>' + label + '</strong>';
  }
}

var __mmQtyTimer = 0;
var __mmQtyDraining = false;

// Drain every pending line-quantity change to the server, ONE request at a
// time. Shopify's cart can't absorb concurrent /cart/change.js writes — each
// overlapping request operates on a stale snapshot and the last response
// silently clobbers the others, so spam-deleting several lines used to leave
// some behind. Serializing through this single drain loop guarantees every
// change lands. Rapid clicks merely move each line's `target`; the loop
// re-scans on every pass, so it always converges on the latest intent.
async function drainCartQty() {
  if (__mmQtyDraining) return; // a drain is already running; it sees new targets
  __mmQtyDraining = true;
  try {
    var guard = 0;
    while (guard++ < 500) {
      // Pick any line whose desired quantity hasn't been sent yet.
      var key = null, p = null;
      for (var k in __mmQtyPending) {
        if (__mmQtyPending[k].target !== __mmQtyPending[k].sent) {
          key = k; p = __mmQtyPending[k]; break;
        }
      }
      if (key === null) break;
      // Mark as sent BEFORE awaiting so a concurrent re-entry won't double-post
      // the same value. We only ever re-send when the *user's* target moves
      // past what we sent — comparing against the server's post-write quantity
      // would loop forever whenever an inventory cap held it below target.
      var sent = p.target;
      p.sent = sent;
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: sent })
        });
      } catch (e) { /* network error — the reconcile fetch below restores truth */ }
      if (p.target === sent) delete __mmQtyPending[key];
    }
  } finally {
    __mmQtyDraining = false;
    // One authoritative refetch repaints exact, discount-aware figures.
    await CartState.fetch();
    // Clicks that landed during the reconcile fetch leave unsent targets.
    var needsAnother = false;
    for (var kk in __mmQtyPending) {
      if (__mmQtyPending[kk].target === __mmQtyPending[kk].sent) delete __mmQtyPending[kk];
      else needsAnother = true;
    }
    if (needsAnother) drainCartQty();
  }
}

function updateCartQty(key, delta) {
  var item = CartState.items.find(function (i) { return i.key === key; });
  if (!item) return;
  var p = __mmQtyPending[key];
  if (!p) {
    p = __mmQtyPending[key] = { target: item.quantity, sent: item.quantity, item: item };
  }
  p.target = Math.max(0, p.target + delta);
  p.item = item;

  // Only project prices optimistically when we can guarantee the server will
  // agree: the cart has no active discount AND the quantity is going down
  // (a reduction can't newly trigger a "buy N for $X" break). In every other
  // case — a discounted cart, or any increase that might cross a threshold —
  // defer to the server and show a brief dimmed state instead of a number we
  // might have to correct.
  var canProject = !CartState.discountActive && delta < 0;
  patchLineDom(key, p.target, canProject);
  if (canProject) {
    patchSubtotalDom();
  } else {
    markMoneyPending(key);
  }

  // A single shared debounce collapses a burst of clicks — across any number
  // of lines — into one drain pass.
  clearTimeout(__mmQtyTimer);
  __mmQtyTimer = setTimeout(drainCartQty, 220);
}

/* ----------------------------------------------------------
   11. NEWSLETTER
   ---------------------------------------------------------- */
function initNewsletter() {
  document.querySelectorAll('[data-action="newsletter-subscribe"]').forEach(function(form) {
    form.addEventListener('submit', async function(e) {
      e.preventDefault();
      var emailInput = form.querySelector('input[type="email"]');
      if (!emailInput || !emailInput.value) return;

      var btn = form.querySelector('button[type="submit"]');
      var original = btn ? btn.textContent : '';
      if (btn) { btn.textContent = '…'; btn.disabled = true; }

      var klaviyoListId = window.MM_KLAVIYO_LIST_ID;

      try {
        if (klaviyoListId) {
          await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + klaviyoListId, {
            method: 'POST',
            headers: { 'content-type': 'application/json', revision: '2023-02-22' },
            body: JSON.stringify({ data: { type: 'subscription', attributes: {
              list_id: klaviyoListId,
              email: emailInput.value
            }}})
          });
        } else {
          var fd = new FormData();
          fd.append('form_type', 'customer');
          fd.append('utf8', '✓');
          fd.append('contact[email]', emailInput.value);
          await fetch('/contact', { method: 'POST', body: fd });
        }
        if (btn) { btn.textContent = '✓ Joined'; }
        emailInput.value = '';
      } catch (err) {
        if (btn) { btn.textContent = 'Error — try again'; }
      } finally {
        setTimeout(function() {
          if (btn) { btn.textContent = original; btn.disabled = false; }
        }, 3000);
      }
    });
  });
}

/* ----------------------------------------------------------
   12. EVENT DELEGATION — central click handler
   ---------------------------------------------------------- */
document.addEventListener('click', function(e) {
  var action = e.target.closest('[data-mm-action]');
  if (!action) return;

  var type = action.dataset.mmAction;

  switch (type) {
    case 'open-cart':
      e.preventDefault();
      CartState.fetch().then(function() { openCart(); });
      break;

    case 'close-cart':
      closeCart();
      break;

    case 'open-drawer':
      openDrawer();
      break;

    case 'close-drawer':
      closeDrawer();
      break;

    case 'add-to-cart':
      // If button is a submit inside a product form, let the form handler take it
      if (action.tagName === 'BUTTON' && action.type === 'submit') {
        break;
      }
      e.preventDefault();
      if (action.__mmLocked) break; // ignore rage-clicks
      action.__mmLocked = true;
      action.disabled = true;
      action.setAttribute('aria-busy', 'true');
      var origLabelEl = action.querySelector('.mm-btn__label') || action;
      var origText = origLabelEl.textContent;
      origLabelEl.textContent = 'Adding…';
      Promise.resolve(addToCart(action.dataset.variants || action.dataset.variant)).finally(function () {
        action.__mmLocked = false;
        action.disabled = false;
        action.removeAttribute('aria-busy');
        origLabelEl.textContent = origText;
      });
      break;

    case 'quick-add':
      e.preventDefault();
      quickAddToCart(action);
      break;

    case 'apply-promo':
      e.preventDefault();
      applyPromoCode();
      break;

    case 'checkout':
      e.preventDefault();
      window.location.href = '/checkout';
      break;

    case 'cart-qty':
      e.preventDefault();
      var key = action.dataset.key;
      var delta = parseInt(action.dataset.delta, 10);
      updateCartQty(key, delta);
      break;

    case 'reviews-page':
      /* Decorative pagination — extend here for async load */
      var allPageBtns = document.querySelectorAll('[data-mm-action="reviews-page"]');
      allPageBtns.forEach(function(btn) { btn.removeAttribute('aria-current'); });
      action.setAttribute('aria-current', 'true');
      break;

    case 'search':
      /* Delegate to Shopify's native search or predictive search */
      if (typeof window.Shopify !== 'undefined' && Shopify.routes) {
        window.location.href = Shopify.routes.search_url || '/search';
      }
      break;
  }
});

/* Scrim clicks close the relevant panel */
document.addEventListener('click', function(e) {
  if (e.target.matches('[data-mm-cart-scrim]')) closeCart();
  if (e.target.matches('[data-mm-drawer-scrim]')) closeDrawer();
});

/* Escape key closes open panels */
document.addEventListener('keydown', function(e) {
  if (e.key !== 'Escape') return;
  if (cartDrawerOpen) closeCart();
  else if (navDrawerOpen) closeDrawer();
});

/* ----------------------------------------------------------
   13. INIT
   ---------------------------------------------------------- */
/* ----------------------------------------------------------
   STICKY HEADER (scroll-aware)
   ---------------------------------------------------------- */
function initStickyHeader() {
  var header = document.getElementById('mm-site-header');
  if (!header) return;
  var mode = header.dataset.mmSticky;
  if (mode !== 'scroll') return; // CSS handles "always" and "never" via [data-mm-sticky] selectors

  var lastY = window.scrollY;
  var DELTA = 8;       // ignore scroll noise smaller than this
  var TOP_BUFFER = 80; // never hide while still near the top
  var ticking = false;

  function update() {
    var y = window.scrollY;
    var diff = y - lastY;
    if (Math.abs(diff) < DELTA) { ticking = false; return; }
    if (y < TOP_BUFFER) {
      header.classList.remove('is-hidden');
    } else if (diff > 0) {
      header.classList.add('is-hidden');     // scrolling down → hide
    } else {
      header.classList.remove('is-hidden');  // scrolling up → show
    }
    lastY = y;
    ticking = false;
  }

  window.addEventListener('scroll', function() {
    if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
  }, { passive: true });
}

function initCartUpsellCarousel() {
  document.querySelectorAll('[data-mm-upsells-layout="carousel"]').forEach(function(wrap) {
    var track = wrap.querySelector('[data-mm-upsells-track]');
    var prev  = wrap.querySelector('[data-mm-upsell-prev]');
    var next  = wrap.querySelector('[data-mm-upsell-next]');
    if (!track) return;

    var step = function() {
      var first = track.querySelector('.mm-cart__upsell');
      if (!first) return 200;
      // Step by item width + gap
      var styles = getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || '10') || 10;
      return first.getBoundingClientRect().width + gap;
    };
    var update = function() {
      if (!prev || !next) return;
      var max = track.scrollWidth - track.clientWidth - 1;
      prev.disabled = track.scrollLeft <= 0;
      next.disabled = track.scrollLeft >= max;
    };

    if (prev) prev.addEventListener('click', function(e) { e.preventDefault(); e.stopPropagation(); track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function(e) { e.preventDefault(); e.stopPropagation(); track.scrollBy({ left:  step(), behavior: 'smooth' }); });
    track.addEventListener('scroll', update, { passive: true });
    update();
  });
}

/* ----------------------------------------------------------
   Theme-editor integration — click a section or block in the
   editor sidebar and the corresponding drawer (cart or mobile
   nav) slides into view automatically. No effect on the live
   storefront.
   ---------------------------------------------------------- */
function initEditorDrawerHooks() {
  // Only run inside the Shopify theme editor preview iframe
  if (!(window.Shopify && Shopify.designMode)) return;

  // Heuristics to map a selected section/block to the right drawer.
  // We open the cart drawer for anything whose id/type/path mentions
  // cart/upsell/shipping/promo/checkout, and the nav drawer for
  // mobile-drawer/menu hits.
  var cartHints   = /cart|upsell|shipping|gift|promo|checkout/i;
  var navHints    = /drawer|hamburger|nav/i;
  var stickyHints = /sticky|hero|buybox|atc/i;

  function probe(detail) {
    if (!detail) return '';
    return [detail.sectionId, detail.blockId, detail.type, detail.id, detail.path].join(' ');
  }

  function pick(detail) {
    var s = probe(detail);
    if (cartHints.test(s))   return 'cart';
    if (navHints.test(s))    return 'nav';
    if (stickyHints.test(s)) return 'sticky';
    // Fall through: also inspect the actual selected DOM element if present
    var el = document.querySelector('[data-shopify-editor-block-selected],[data-shopify-editor-section-selected]');
    if (el && el.closest && el.closest('#mm-cart'))        return 'cart';
    if (el && el.closest && el.closest('#mm-drawer'))      return 'nav';
    if (el && el.closest && el.closest('#mm-sticky-atc'))  return 'sticky';
    return '';
  }

  function showSticky() {
    var bar = document.getElementById('mm-sticky-atc');
    if (bar) bar.setAttribute('data-visible', '');
  }
  function hideSticky() {
    var bar = document.getElementById('mm-sticky-atc');
    if (bar && !bar.hasAttribute('data-editor-pinned')) bar.removeAttribute('data-visible');
  }

  function handleSelect(e) {
    var which = pick(e.detail);
    if (which === 'cart'   && typeof openCart   === 'function') openCart();
    if (which === 'nav'    && typeof openDrawer === 'function') openDrawer();
    if (which === 'sticky') showSticky();
  }

  function handleDeselect() {
    if (typeof closeCart   === 'function') closeCart();
    if (typeof closeDrawer === 'function') closeDrawer();
    hideSticky();
  }

  document.addEventListener('shopify:section:select', handleSelect);
  document.addEventListener('shopify:block:select',   handleSelect);
  document.addEventListener('shopify:section:deselect', handleDeselect);
  document.addEventListener('shopify:block:deselect',   handleDeselect);
}

/* ----------------------------------------------------------
   10c. SUBSCRIPTION WIDGET — radio cards + frequency select.
   Toggles hidden line-item-property inputs (disabled = not posted).
   ---------------------------------------------------------- */
function initSubscriptionWidgets() {
  // Matches Shopify-formatted money strings ($29.99, £1,299.00, etc.)
  var MONEY_RE = /([$€£¥₹]|kr|CHF|₩|CAD|USD)\s?[\d,]+(?:\.\d+)?/;

  function syncAtc(priceText) {
    if (!priceText) return;
    document.querySelectorAll('[data-mm-action="add-to-cart"] .mm-btn__label').forEach(function(el) {
      var t = el.textContent;
      if (MONEY_RE.test(t)) {
        el.textContent = t.replace(MONEY_RE, priceText);
      } else if (t.indexOf('—') === -1) {
        el.textContent = t.trim() + ' — ' + priceText;
      }
    });
    document.querySelectorAll('.mm-sticky-atc__price').forEach(function(el) {
      el.textContent = priceText;
    });
  }

  function renderTokens(tpl, vals) {
    if (!tpl) return '';
    return tpl
      .replace(/\{discount\}/g, vals.discount || '')
      .replace(/\{percent\}/g,  vals.percent  || '')
      .replace(/\{price\}/g,    vals.price    || '')
      .replace(/\{was\}/g,      vals.was      || '')
      .replace(/\{saved\}/g,    vals.saved    || '');
  }

  document.querySelectorAll('[data-mm-sub]').forEach(function(group) {
    var hasPlans = group.getAttribute('data-mm-sub-has-plans') === '1';
    var cards    = group.querySelectorAll('[data-mm-sub-card]');
    var planEl   = group.querySelector('[data-mm-sub-plan-input]');
    var propOne  = group.querySelector('[data-mm-sub-prop="onetime"]');
    var propSub  = group.querySelector('[data-mm-sub-prop="subscribe"]');
    var freqSel  = group.querySelector('[data-mm-sub-freq-select]');
    var priceEl  = group.querySelector('[data-mm-sub-disc-price]');
    var tplEls   = group.querySelectorAll('[data-mm-sub-tpl]');
    var oneCardPriceEl = group.querySelector('[data-mm-sub-card="onetime"] .mm-sub__price');

    function activePriceText(mode) {
      if (mode === 'subscribe' && priceEl) return priceEl.textContent.trim();
      if (mode === 'onetime'   && oneCardPriceEl) return oneCardPriceEl.textContent.trim();
      return null;
    }

    function select(mode) {
      cards.forEach(function(c) {
        var match = c.getAttribute('data-mm-sub-card') === mode;
        c.classList.toggle('is-selected', match);
        var radio = c.querySelector('.mm-sub__radio-input');
        if (radio) radio.checked = match;
      });
      if (planEl)  planEl.disabled  = (mode !== 'subscribe');
      if (propOne) propOne.disabled = (mode !== 'onetime');
      if (propSub) propSub.disabled = (mode !== 'subscribe');
      syncAtc(activePriceText(mode));
    }

    cards.forEach(function(card) {
      card.addEventListener('click', function(e) {
        if (e.target.closest('.mm-sub__freq-select')) return;
        select(card.getAttribute('data-mm-sub-card'));
      });
      card.addEventListener('keydown', function(e) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          select(card.getAttribute('data-mm-sub-card'));
        }
      });
    });

    if (freqSel) {
      freqSel.addEventListener('change', function() {
        var opt = freqSel.options[freqSel.selectedIndex];
        if (!opt) return;
        if (hasPlans && planEl) planEl.value = opt.value; // selling_plan ID
        var price    = opt.getAttribute('data-price');
        var was      = opt.getAttribute('data-was');
        var pct      = opt.getAttribute('data-pct');
        var discount = opt.getAttribute('data-discount') || (pct ? pct + '%' : '');
        var saved    = opt.getAttribute('data-saved');
        if (price && priceEl) priceEl.textContent = price;
        var vals = {
          discount: discount,
          percent:  pct,
          price:    price,
          was:      was,
          saved:    saved
        };
        tplEls.forEach(function(el) {
          el.textContent = renderTokens(el.getAttribute('data-mm-sub-tpl'), vals);
        });
        // If subscribe is currently selected, push the new price to the ATC bar
        var subCard = group.querySelector('[data-mm-sub-card="subscribe"].is-selected');
        if (subCard && price) syncAtc(price);
      });
    }

    // Initial sync — server rendered ATC with base variant price; if the
    // default selection is "subscribe", reflect the discounted price now.
    var initialMode = (group.getAttribute('data-mm-sub-default') === 'onetime') ? 'onetime' : 'subscribe';
    syncAtc(activePriceText(initialMode));
  });
}

document.addEventListener('DOMContentLoaded', function() {
  // Critical path — wire interactions immediately
  initCountdown();
  initMiniReviews();
  initFaq();
  initStickyAtc();
  initStickyHeader();
  initNewsletter();
  initProductForms();
  initVariantPickers();
  initSubscriptionWidgets();
  initCartUpsellCarousel();
  initEditorDrawerHooks();

  // Reservation banner: prime data attrs so it renders correctly even
  // before the cart is opened. If the drawer is pinned open (theme
  // editor) or already open at boot, start ticking immediately.
  CartReserve.init();
  var __mmCartEl = document.getElementById('mm-cart');
  if (__mmCartEl && __mmCartEl.hasAttribute('data-open')) CartReserve.start();

  // Non-critical — fetch cart state during idle so it doesn't block LCP
  var fetchCart = function() { CartState.fetch(); };
  if ('requestIdleCallback' in window) {
    requestIdleCallback(fetchCart, { timeout: 2000 });
  } else {
    setTimeout(fetchCart, 800);
  }
});
